import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { gymApi, isNetworkRpcError } from '@/lib/api';
import { ClientReward, RewardLeaderboard } from '@/types';
import { reportError } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';
import { Gift, Loader2, RotateCw, Flame, Trophy, ChevronLeft, ChevronRight, Check, KeyRound, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ClientRewardsDialogProps {
    gymId: string;
    clientId: string;
    clientName?: string;
    storageKey?: string;
    refreshToken?: number;
}

interface SpinResult {
    result?: string;
    reason?: string;
    message?: string;
    prize_label?: string | null;
    segment_index?: number | null;
    retry_after_seconds?: number;
}

const WHEEL_COLORS = ['#f97316', '#0ea5e9', '#22c55e', '#a855f7', '#ef4444', '#eab308', '#14b8a6', '#ec4899'];

const polar = (cx: number, cy: number, r: number, angleDeg: number) => {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
};

const buildSegments = (weights: number[]) => {
    const total = weights.reduce((s, w) => s + (w > 0 ? w : 0), 0) || 1;
    let cursor = 0;
    return weights.map((w) => {
        const start = cursor;
        const sweep = ((w > 0 ? w : 0) / total) * 360;
        cursor += sweep;
        return { start, end: cursor, mid: start + sweep / 2 };
    });
};

const rankClass = (rank: number) =>
    rank === 1 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40'
        : rank === 2 ? 'bg-zinc-400/20 text-zinc-200 border-zinc-400/40'
            : rank === 3 ? 'bg-orange-700/20 text-orange-400 border-orange-700/40'
                : 'bg-zinc-800 text-zinc-400 border-zinc-700';

const initials = (name: string) =>
    name.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const isCodeFailure = (reason?: string) =>
    reason === 'invalid' || reason === 'expired' || reason === 'locked' || reason === 'no_code' || reason === 'no_membership';

export function ClientRewardsDialog({ gymId, clientId, clientName, storageKey, refreshToken }: ClientRewardsDialogProps) {
    const [rewards, setRewards] = useState<ClientReward[]>([]);
    const [hasRewards, setHasRewards] = useState(false);
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [index, setIndex] = useState(0);
    const [spinning, setSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [spinResult, setSpinResult] = useState<SpinResult | null>(null);
    const [leaderboard, setLeaderboard] = useState<RewardLeaderboard | null>(null);
    const [codeRequired, setCodeRequired] = useState(true);
    const [accessCode, setAccessCode] = useState('');
    const [codeInput, setCodeInput] = useState('');
    const [codeError, setCodeError] = useState<string | null>(null);
    const [verifying, setVerifying] = useState(false);
    const [lockSeconds, setLockSeconds] = useState(0);
    const { toast } = useToast();
    const prevEnteredRef = useRef<Record<string, boolean>>({});
    const verifyingRef = useRef(false);
    const accessCodeRef = useRef('');

    const seenStorageKey = storageKey || `gympro_rewards_seen_${gymId}_${clientId}`;

    const readSeen = (): string[] => {
        try {
            return JSON.parse(localStorage.getItem(seenStorageKey) || '[]');
        } catch {
            return [];
        }
    };

    const markSeenLocally = (rewardId: string) => {
        try {
            const seen = new Set(readSeen());
            seen.add(rewardId);
            localStorage.setItem(seenStorageKey, JSON.stringify(Array.from(seen)));
        } catch {
            // localStorage unavailable; ignore
        }
    };

    const rememberCode = (code: string) => {
        accessCodeRef.current = code;
        setAccessCode(code);
    };

    const clearCode = () => {
        accessCodeRef.current = '';
        setAccessCode('');
    };

    const loadRewards = async (code: string, cancelled = false) => {
        setLoading(true);
        try {
            const res = await gymApi.getClientRewards(gymId, clientId, code);
            if (cancelled) return;

            if (!res.success) {
                clearCode();
                setRewards([]);
                setCodeError(res.message || 'El código cambió. Pídelo de nuevo en recepción.');
                setLockSeconds(res.retryAfterSeconds || 0);
                setIsOpen(true);
                return;
            }

            // Celebratory feedback when a streak is completed in this check-in
            const prevEntered = prevEnteredRef.current;
            res.rewards.forEach((r) => {
                if (r.type === 'streak' && r.entered && prevEntered[r.reward_id] === false) {
                    toast({
                        title: '¡Racha completada!',
                        description: `Ya estás participando en la rifa: ${r.prizes[0]?.label || 'premio'}`,
                    });
                }
            });
            const nextEntered: Record<string, boolean> = {};
            res.rewards.forEach((r) => { nextEntered[r.reward_id] = !!r.entered; });
            prevEnteredRef.current = nextEntered;

            const seen = new Set(readSeen());
            const pending = res.rewards.filter((r) => !seen.has(r.reward_id));
            setRewards(pending);
            setIndex(0);
            setCodeError(null);
            setLockSeconds(0);
            if (pending.length > 0) {
                setIsOpen(true);
            } else {
                setIsOpen(false);
            }
        } catch (error) {
            if (cancelled) return;
            if (!isNetworkRpcError(error)) {
                reportError(error, { operation: 'client_rewards_load', area: 'crm', extra: { gym_id: gymId } });
            }
            clearCode();
            setRewards([]);
            setCodeError('No se pudieron cargar las recompensas. Intenta de nuevo.');
            setIsOpen(true);
        } finally {
            if (!cancelled) setLoading(false);
        }
    };

    useEffect(() => {
        if (!gymId || !clientId) return;
        let cancelled = false;

        const check = async () => {
            setCheckingAvailability(true);
            try {
                const res = await gymApi.hasActiveRewards(gymId, clientId);
                if (cancelled) return;

                if (!res.hasRewards) {
                    setHasRewards(false);
                    setRewards([]);
                    setIsOpen(false);
                    return;
                }

                setHasRewards(true);
                setCodeRequired(res.codeRequired !== false);

                if (accessCodeRef.current || res.codeRequired === false) {
                    if (res.codeRequired === false && !accessCodeRef.current) {
                        setIsOpen(true);
                    }
                    await loadRewards(accessCodeRef.current || '', cancelled);
                } else {
                    setIsOpen(true);
                }
            } catch (error) {
                if (!cancelled && !isNetworkRpcError(error)) {
                    reportError(error, { operation: 'client_rewards_available', area: 'crm', extra: { gym_id: gymId } });
                }
            } finally {
                if (!cancelled) setCheckingAvailability(false);
            }
        };

        check();
        return () => {
            cancelled = true;
        };
    }, [gymId, clientId, refreshToken]);

    const current = rewards[index];

    const segments = useMemo(() => {
        if (!current || current.type !== 'wheel' || current.prizes.length === 0) return [];
        return buildSegments(current.prizes.map((p) => p.weight));
    }, [current]);

    useEffect(() => {
        prevEnteredRef.current = {};
        setLeaderboard(null);
        setHasRewards(false);
        setRewards([]);
        setIndex(0);
        setSpinResult(null);
        setRotation(0);
        setCodeRequired(true);
        setCodeInput('');
        setCodeError(null);
        setLockSeconds(0);
    }, [gymId, clientId]);

    const unlocked = !!accessCode || !codeRequired;

    useEffect(() => {
        if (!current || current.type !== 'streak' || !unlocked) {
            setLeaderboard(null);
            return;
        }
        let cancelled = false;
        gymApi.getRewardLeaderboard(gymId, current.reward_id, clientId, accessCode, 5)
            .then((lb) => { if (!cancelled) setLeaderboard(lb); })
            .catch((error) => {
                if (!isNetworkRpcError(error)) {
                    reportError(error, { operation: 'rewards_leaderboard', area: 'crm', extra: { reward_id: current.reward_id } });
                }
            });
        return () => { cancelled = true; };
    }, [gymId, clientId, current, accessCode, unlocked]);

    useEffect(() => {
        if (lockSeconds <= 0) return;
        const timer = window.setTimeout(() => {
            setLockSeconds((s) => Math.max(0, s - 1));
        }, 1000);
        return () => window.clearTimeout(timer);
    }, [lockSeconds]);

    const handleCodeFailure = (result?: { message?: string; retry_after_seconds?: number; reason?: string }) => {
        if (result?.reason === 'no_membership') {
            clearCode();
            setRewards([]);
            setSpinResult(null);
            setRotation(0);
            setIsOpen(false);
            toast({
                title: 'Membresía inactiva',
                description: result.message || 'Necesitas una membresía activa para participar en recompensas.',
                variant: 'destructive',
            });
            return;
        }

        clearCode();
        setRewards([]);
        setSpinResult(null);
        setRotation(0);
        setCodeError(result?.message || 'El código cambió. Pídelo de nuevo en recepción.');
        setLockSeconds(result?.retry_after_seconds || 0);
        setIsOpen(true);
    };

    const handleVerify = async (value?: string) => {
        const code = (value ?? codeInput).trim();
        if (code.length !== 4 || verifyingRef.current) return;

        verifyingRef.current = true;
        setVerifying(true);
        setCodeError(null);
        try {
            const res = await gymApi.verifyRewardCode(gymId, clientId, code);

            if (!res.success || !res.valid) {
                setCodeInput('');
                if (res.reason === 'no_membership') {
                    setIsOpen(false);
                    toast({
                        title: 'Membresía inactiva',
                        description: res.message || 'Necesitas una membresía activa para participar en recompensas.',
                        variant: 'destructive',
                    });
                    return;
                }
                setCodeError(res.message || 'Código inválido. Pídelo en recepción.');
                if (res.reason === 'locked' && res.retry_after_seconds) {
                    setLockSeconds(res.retry_after_seconds);
                }
                return;
            }

            rememberCode(code);
            setCodeInput('');
            setLockSeconds(0);
            await loadRewards(code);
        } catch (error) {
            if (!isNetworkRpcError(error)) {
                reportError(error, { operation: 'client_rewards_verify_code', area: 'crm', extra: { gym_id: gymId } });
            }
            setCodeError('No se pudo verificar el código. Intenta de nuevo.');
        } finally {
            verifyingRef.current = false;
            setVerifying(false);
        }
    };

    const handleCodeChange = (value: string) => {
        const next = value.replace(/\D/g, '').slice(0, 4);
        setCodeInput(next);
        setCodeError(null);
        if (next.length === 4) {
            void handleVerify(next);
        }
    };

    const handleSpin = async () => {
        if (!current || spinning || spinResult || !unlocked) return;
        setSpinning(true);
        try {
            const result: SpinResult = await gymApi.playWheel(gymId, clientId, current.reward_id, accessCode);

            if (result?.success === false && isCodeFailure(result.reason)) {
                setSpinning(false);
                handleCodeFailure(result);
                return;
            }

            const segIndex = result?.segment_index ?? 0;
            const seg = segments[segIndex];
            setRotation((prev) => {
                const base = Math.floor(prev / 360) * 360;
                return base + 360 * 5 + (360 - (seg ? seg.mid : 0));
            });
            window.setTimeout(() => {
                setSpinResult(result);
                setSpinning(false);
                markSeenLocally(current.reward_id);
                gymApi.markRewardSeen(gymId, clientId, current.reward_id, accessCode).catch(() => undefined);
            }, 4200);
        } catch (error) {
            setSpinning(false);
            if (!isNetworkRpcError(error)) {
                reportError(error, { operation: 'client_rewards_spin', area: 'crm', extra: { reward_id: current.reward_id } });
            }
            toast({ title: 'Error', description: 'No se pudo girar la ruleta.', variant: 'destructive' });
        }
    };

    const dismissCurrent = () => {
        if (!current) return;
        markSeenLocally(current.reward_id);
        if (unlocked) {
            gymApi.markRewardSeen(gymId, clientId, current.reward_id, accessCode).catch(() => undefined);
        }

        const remaining = rewards.filter((_, i) => i !== index);
        setRewards(remaining);
        setSpinResult(null);
        setRotation(0);
        if (remaining.length === 0) {
            setIsOpen(false);
        } else {
            setIndex((i) => Math.min(i, remaining.length - 1));
        }
    };

    if (!gymId || !clientId || checkingAvailability || !hasRewards) return null;

    const showGate = isOpen && !unlocked;
    const showRewards = isOpen && unlocked && rewards.length > 0 && !!current;
    const showLoading = isOpen && unlocked && loading;

    if (!showGate && !showRewards && !showLoading) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !spinning && setIsOpen(open)}>
            <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:w-full sm:max-w-[440px] sm:p-6">
                {showGate ? (
                    <>
                        <DialogHeader className="pr-8">
                            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                                <KeyRound className="h-5 w-5 text-primary" />
                                Código de recompensas
                            </DialogTitle>
                            <DialogDescription>
                                Pídelo en recepción para desbloquear tus recompensas.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="py-4 space-y-4">
                            <div className="flex justify-center">
                                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10 sm:h-16 sm:w-16">
                                    {lockSeconds > 0 ? (
                                        <Lock className="h-6 w-6 text-primary sm:h-7 sm:w-7" />
                                    ) : (
                                        <KeyRound className="h-6 w-6 text-primary sm:h-7 sm:w-7" />
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Input
                                    value={codeInput}
                                    onChange={(e) => handleCodeChange(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') void handleVerify();
                                    }}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={4}
                                    autoFocus
                                    disabled={verifying || lockSeconds > 0}
                                    placeholder="••••"
                                    aria-label="Código de 4 dígitos"
                                    className="h-14 text-center text-3xl font-black tracking-[0.4em] placeholder:tracking-[0.3em] sm:h-16 sm:text-4xl sm:tracking-[0.55em] sm:placeholder:tracking-[0.35em]"
                                />
                                <p className="text-xs text-muted-foreground text-center">
                                    Escríbelo tal como aparece en recepción.
                                </p>
                            </div>

                            {codeError && (
                                <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-center text-sm font-bold text-destructive">
                                    {codeError}
                                    {lockSeconds > 0 && ` (${lockSeconds}s)`}
                                </div>
                            )}

                            <Button
                                variant="gradient"
                                className="w-full h-12 rounded-2xl font-black"
                                onClick={() => void handleVerify()}
                                disabled={codeInput.length !== 4 || verifying || lockSeconds > 0}
                            >
                                {verifying ? <Loader2 className="h-5 w-5 animate-spin" /> : 'DESBLOQUEAR'}
                            </Button>

                            <p className="text-[11px] leading-tight text-muted-foreground text-center">
                                El código cambia cada 24 horas.
                            </p>
                        </div>
                    </>
                ) : showLoading ? (
                    <>
                        <DialogHeader className="pr-8">
                            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                                <Gift className="h-5 w-5 text-primary" />
                                Cargando recompensas
                            </DialogTitle>
                        </DialogHeader>
                        <div className="py-10 flex justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    </>
                ) : current ? (
                    <>
                        <DialogHeader className="pr-8">
                            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                                <Gift className="h-5 w-5 text-primary" />
                                {clientName ? `${clientName.split(' ')[0]}, tienes premios` : 'Tienes premios'}
                            </DialogTitle>
                            <DialogDescription>{current.title}</DialogDescription>
                        </DialogHeader>

                        <div className="py-2 space-y-4">
                            <div className="flex items-center gap-2">
                                {current.type === 'wheel' ? (
                                    <Badge variant="outline" className="gap-1"><RotateCw className="h-3 w-3" /> Ruleta</Badge>
                                ) : (
                                    <Badge variant="outline" className="gap-1"><Flame className="h-3 w-3" /> Racha de asistencia</Badge>
                                )}
                                {rewards.length > 1 && (
                                    <span className="text-xs text-muted-foreground ml-auto">
                                        {index + 1} / {rewards.length}
                                    </span>
                                )}
                            </div>

                            {current.description && (
                                <p className="text-sm text-muted-foreground">{current.description}</p>
                            )}

                            {current.type === 'wheel' ? (
                                <div className="flex flex-col items-center gap-4">
                                    <div className="relative aspect-square w-[min(220px,58vw)]">
                                        <div className="absolute left-1/2 -top-1 -translate-x-1/2 z-10 text-primary">
                                            <svg width="22" height="18" viewBox="0 0 22 18">
                                                <polygon points="11,18 0,0 22,0" fill="currentColor" />
                                            </svg>
                                        </div>
                                        <svg
                                            viewBox="0 0 200 200"
                                            className="h-full w-full drop-shadow-xl"
                                            style={{
                                                transform: `rotate(${rotation}deg)`,
                                                transformOrigin: '100px 100px',
                                                transition: spinning
                                                    ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)'
                                                    : 'transform 0.3s ease',
                                            }}
                                        >
                                            <circle cx="100" cy="100" r="98" fill="#111827" />
                                            {current.prizes.map((prize, i) => {
                                                const seg = segments[i];
                                                if (!seg) return null;
                                                const [x1, y1] = polar(100, 100, 94, seg.start);
                                                const [x2, y2] = polar(100, 100, 94, seg.end);
                                                const large = seg.end - seg.start > 180 ? 1 : 0;
                                                const [lx, ly] = polar(100, 100, 62, seg.mid);
                                                return (
                                                    <g key={prize.id}>
                                                        <path
                                                            d={`M100,100 L${x1},${y1} A94,94 0 ${large} 1 ${x2},${y2} Z`}
                                                            fill={WHEEL_COLORS[i % WHEEL_COLORS.length]}
                                                            stroke="#111827"
                                                            strokeWidth="1"
                                                        />
                                                        <text
                                                            x={lx}
                                                            y={ly}
                                                            fontSize="9"
                                                            fontWeight="700"
                                                            fill="#fff"
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            transform={`rotate(${seg.mid}, ${lx}, ${ly})`}
                                                        >
                                                            {prize.label.length > 12 ? `${prize.label.slice(0, 11)}…` : prize.label}
                                                        </text>
                                                    </g>
                                                );
                                            })}
                                            <circle cx="100" cy="100" r="16" fill="#111827" stroke="#f97316" strokeWidth="3" />
                                        </svg>
                                    </div>

                                    {spinResult ? (
                                        <div className="text-center space-y-1">
                                            <div className="flex items-center justify-center gap-2 text-green-500 font-black text-lg">
                                                <Trophy className="h-5 w-5" />
                                                {spinResult.prize_label || spinResult.message || 'Resultado'}
                                            </div>
                                            <p className="text-xs text-muted-foreground">Muestra este resultado en recepción para reclamar tu premio.</p>
                                        </div>
                                    ) : (
                                        <Button
                                            variant="gradient"
                                            className="w-full h-12 rounded-2xl font-black"
                                            onClick={handleSpin}
                                            disabled={spinning}
                                        >
                                            {spinning ? <Loader2 className="h-5 w-5 animate-spin" /> : '¡GIRAR!'}
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3 py-1 sm:space-y-4 sm:py-2">
                                    <div className="flex items-center justify-center">
                                        <div className={cn('h-16 w-16 rounded-full border-2 flex items-center justify-center sm:h-20 sm:w-20',
                                            current.won ? 'bg-yellow-500/15 border-yellow-500/50' : 'bg-primary/10 border-primary/40')}>
                                            {current.won ? <Trophy className="h-8 w-8 text-yellow-400 sm:h-10 sm:w-10" /> : <Flame className="h-8 w-8 text-primary sm:h-10 sm:w-10" />}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm font-bold">
                                            <span>{current.progress_count ?? 0} días</span>
                                            <span>{current.progress_target ?? 0} días</span>
                                        </div>
                                        <Progress
                                            value={Math.min(100, ((current.progress_count ?? 0) / (current.progress_target || 1)) * 100)}
                                        />
                                    </div>

                                    <div className={cn('rounded-2xl border p-3 text-center text-sm font-bold',
                                        current.won ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300'
                                            : current.entered ? 'border-primary/40 bg-primary/10 text-primary'
                                                : 'border-zinc-800 bg-zinc-900/40 text-zinc-400')}>
                                        {current.won
                                            ? (current.claimed
                                                ? '¡Ganaste la rifa! Tu premio ya fue reclamado.'
                                                : `¡Ganaste la rifa! Premio: ${current.prizes[0]?.label || 'premio'}. Muéstralo en recepción.`)
                                            : current.entered
                                                ? '¡Estás participando en la rifa! El ganador se anunciará aquí.'
                                                : `Asiste en ${Math.max(0, (current.progress_target ?? 0) - (current.progress_count ?? 0))} día(s) más para entrar a la rifa: ${current.prizes[0]?.label || 'premio'}`}
                                    </div>

                                    {leaderboard && leaderboard.top.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
                                                <Trophy className="h-3 w-3" /> Líderes de la racha
                                            </p>
                                            <div className="space-y-1.5">
                                                {leaderboard.top.map((row) => (
                                                    <div key={row.rank} className={cn('flex items-center gap-2 rounded-xl border px-2 py-2 sm:gap-3 sm:px-3',
                                                        row.is_current ? 'border-primary bg-primary/10' : 'border-zinc-800 bg-zinc-900/40')}>
                                                        <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-black', rankClass(row.rank))}>
                                                            {row.rank}
                                                        </span>
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-xs font-black text-zinc-200">
                                                            {initials(row.display_name)}
                                                        </span>
                                                        <span className="flex-1 truncate text-sm font-bold">
                                                            {row.display_name}{row.is_current ? ' (tú)' : ''}
                                                        </span>
                                                        {row.is_winner && <Trophy className="h-4 w-4 text-yellow-400" />}
                                                        <span className="text-sm font-black text-primary">{row.counted_days}d</span>
                                                    </div>
                                                ))}
                                                {leaderboard.me && leaderboard.me.rank > leaderboard.top.length && (
                                                    <div className="flex items-center gap-2 rounded-xl border border-primary/50 bg-primary/5 px-2 py-2 sm:gap-3 sm:px-3">
                                                        <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-black', rankClass(leaderboard.me.rank))}>
                                                            {leaderboard.me.rank}
                                                        </span>
                                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-xs font-black text-zinc-200">
                                                            {initials(leaderboard.me.display_name)}
                                                        </span>
                                                        <span className="flex-1 truncate text-sm font-bold">Tu posición</span>
                                                        <span className="text-sm font-black text-primary">{leaderboard.me.counted_days}d</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <DialogFooter className="flex-col gap-3 sm:flex-col">
                            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex justify-center gap-1 sm:justify-start">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={index === 0 || spinning}
                                        onClick={() => { setIndex((i) => i - 1); setSpinResult(null); setRotation(0); }}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={index >= rewards.length - 1 || spinning}
                                        onClick={() => { setIndex((i) => i + 1); setSpinResult(null); setRotation(0); }}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                                <Button variant="outline" className="w-full sm:w-auto" onClick={dismissCurrent} disabled={spinning}>
                                    <Check className="h-4 w-4 mr-2" />
                                    <span className="sm:hidden">Entendido</span>
                                    <span className="hidden sm:inline">Entendido, no volver a mostrar</span>
                                </Button>
                            </div>
                            <p className="text-[11px] leading-tight text-muted-foreground text-center">
                                {rewards.length > 1
                                    ? 'Al pulsar "Entendido" dejarás de ver esta oferta en tus próximos ingresos. Usa las flechas para ver las demás.'
                                    : 'Al pulsar "Entendido" dejarás de ver esta oferta en tus próximos ingresos.'}
                            </p>
                        </DialogFooter>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
